import React, { useCallback, useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import { Link } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import {
  AlertTriangle,
  ArrowUpDown,
  BarChart3,
  Calendar,
  CalendarRange,
  Clock,
  Download,
  ExternalLink,
  IndianRupee,
  LayoutGrid,
  List,
  Loader2,
  MoreVertical,
  Pencil,
  Phone,
  Search,
  Smartphone,
  Trash2,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  X,
} from "lucide-react";
import { db } from "../firebase";
import { downloadCsv } from "../utils/csvExport";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { MONTHLY_AUTOS_BRANDS } from "../services/monthlyAutosReportService";
import { ATTENDANCE_BRANCHES } from "../config/attendanceBranches";
import { canAccessAppBranch } from "../config/appBranches";
import { hasPermission } from "../config/permissions";
import { resolveCollectionName } from "../services/attendanceCollections";

/** Matches Flutter branch keys → shop label for syncQueue. */
const SHOP_LABEL_FOR_SYNC = {
  main: "🏠 Shop 1",
  branch1: "🏬 Shop 2",
  branch2: "Branch",
};

const BRANCH_TABS = [
  { key: "main", label: "Main" },
  { key: "branch1", label: "Spareway" },
  { key: "branch2", label: "Branch" },
];

/** Same order as {@link ATTENDANCE_BRANCHES} / branch tabs — maps to `users.allowedBranches` values */
const BRANCH_TAB_APP_VALUES = ["Main", "Branch 2", "Branch 3"];

const STATUS_OPTIONS = ["All", "Working", "On Leave", "Left"];
const GENDER_OPTIONS = ["male", "female", "other"];
const EMP_TYPES_BASE = ["Permanent", "Contract", "Temporary", "Probation", "Intern", "Other"];

/** Department / counter alignment (two-wheeler brands + other). */
const DEPARTMENT_BASE = [...MONTHLY_AUTOS_BRANDS, "OTHER"];

function toDateFromDynamic(v) {
  if (v == null) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function statusBadgeClasses(s) {
  switch (String(s || "").toLowerCase()) {
    case "working":
      return "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/90";
    case "on leave":
      return "bg-amber-50 text-amber-950 ring-1 ring-amber-200/90";
    case "left":
      return "bg-rose-50 text-rose-900 ring-1 ring-rose-200/90";
    default:
      return "bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200/90";
  }
}

function formatDoj(d) {
  if (!d) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const y = String(d.getFullYear()).slice(-2);
  return `${day}/${mo}/${y}`;
}

function formatSalary(data) {
  const raw = data?.monthlySalary ?? data?.MonthlySalary ?? data?.salary ?? data?.Salary;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function employeeInitial(name) {
  return String(name ?? "?").trim().slice(0, 1).toUpperCase();
}

const SORT_KEYS = [
  { value: "name", label: "Name" },
  { value: "id", label: "Employee ID" },
  { value: "department", label: "Department" },
  { value: "status", label: "Status" },
  { value: "doj", label: "Date of joining" },
];

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export default function EmployeesPage() {
  const storedUser = useMemo(() => getStoredUser(), []);
  /** Same gate as /payroll/employees route (payroll permission); not only admin/accounts. */
  const canWrite = hasPermission(storedUser, "payroll");

  const visibleBranchTabs = useMemo(
    () =>
      BRANCH_TABS.filter((_, i) => canAccessAppBranch(storedUser, BRANCH_TAB_APP_VALUES[i])),
    [storedUser]
  );

  const [branchKey, setBranchKey] = useState("main");
  const [empCollectionName, setEmpCollectionName] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState("All");
  const [departmentFilter, setDepartmentFilter] = useState("All");
  const [viewMode, setViewMode] = useState("table");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const searchInputRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    EmployeeName: "",
    employeeId: "",
    employeeCode: "",
    phone: "",
    CompanyId: "1",
    Gender: "male",
    Status: "Working",
    EmployementType: "Permanent",
    department: "",
    monthlySalary: "",
    expectedStartTime: "09:30",
    lunchMinutes: "60",
    DOJ: null,
    portalPin: "",
  });

  useEffect(() => {
    if (!visibleBranchTabs.length) return;
    if (!visibleBranchTabs.some((b) => b.key === branchKey)) {
      setBranchKey(visibleBranchTabs[0].key);
    }
  }, [visibleBranchTabs, branchKey]);

  const branchIndex = BRANCH_TABS.findIndex((b) => b.key === branchKey);
  const attendanceBranch = ATTENDANCE_BRANCHES[branchIndex >= 0 ? branchIndex : 0];

  const resolveEmpCollection = useCallback(async () => {
    const idx = BRANCH_TABS.findIndex((b) => b.key === branchKey);
    const b = ATTENDANCE_BRANCHES[idx >= 0 ? idx : 0];
    const name = await resolveCollectionName(b.employeeCandidates || []);
    setEmpCollectionName(name || b.employeeCandidates?.[0] || null);
  }, [branchKey]);

  useEffect(() => {
    resolveEmpCollection();
  }, [resolveEmpCollection]);

  useEffect(() => {
    setOpenMenuId(null);
  }, [branchKey]);

  useEffect(() => {
    if (!modalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!deleteTarget) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDeleteTarget(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteTarget]);

  useEffect(() => {
    if (!detailDoc) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDetailDoc(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailDoc]);

  useEffect(() => {
    if (!empCollectionName) {
      setDocs([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const colRef = collection(db, empCollectionName);
    const unsub = onSnapshot(colRef, (snap) => {
      const list = [...snap.docs].sort((a, b) => {
        const na = String(a.data()?.EmployeeName || "").toLowerCase();
        const nb = String(b.data()?.EmployeeName || "").toLowerCase();
        return na.localeCompare(nb);
      });
      setDocs(list);
      setLoading(false);
    });
    return () => unsub();
  }, [empCollectionName]);

  const departmentOptions = useMemo(() => {
    const set = new Set();
    for (const d of docs) {
      const dept = String(d.data()?.department ?? d.data()?.Department ?? "").trim();
      if (dept) set.add(dept);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [docs]);

  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    return docs.filter((d) => {
      const data = d.data() || {};
      const name = String(data.EmployeeName ?? "").toLowerCase();
      const id = String(data.employeeId ?? "").toLowerCase();
      const empCode = String(data.employeeCode ?? data.EmployeeCode ?? "").toLowerCase();
      const phone = String(data.phone ?? "").toLowerCase();
      const dept = String(data.department ?? data.Department ?? "").trim();
      const deptLower = dept.toLowerCase();
      const status = String(data.Status ?? "");
      const matchText =
        !q || name.includes(q) || id.includes(q) || empCode.includes(q) || phone.includes(q) || deptLower.includes(q);
      const matchStatus = statusFilter === "All" || status === statusFilter;
      const matchDept = departmentFilter === "All" || dept === departmentFilter;
      return matchText && matchStatus && matchDept;
    });
  }, [docs, deferredSearch, statusFilter, departmentFilter]);

  const sortedFiltered = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (d) => {
      const data = d.data() || {};
      switch (sortKey) {
        case "name":
          return String(data.EmployeeName ?? "").toLowerCase();
        case "id":
          return String(data.employeeId ?? "").toLowerCase();
        case "department":
          return String(data.department ?? data.Department ?? "").toLowerCase();
        case "status":
          return String(data.Status ?? "").toLowerCase();
        case "doj": {
          const t = toDateFromDynamic(data.DOJ);
          return t ? t.getTime() : 0;
        }
        default:
          return "";
      }
    };
    arr.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { sensitivity: "base" }) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => {
    if (openMenuId == null) return undefined;
    const onDown = (e) => {
      if (!e.target.closest?.("[data-employee-action-menu]")) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openMenuId]);

  const stats = useMemo(() => {
    let working = 0;
    let onLeave = 0;
    let left = 0;
    docs.forEach((d) => {
      const s = String(d.data()?.Status ?? "").toLowerCase();
      if (s === "working") working += 1;
      else if (s === "on leave") onLeave += 1;
      else if (s === "left") left += 1;
    });
    return { total: docs.length, working, onLeave, left };
  }, [docs]);

  const enqueueDeviceSync = async (employeeId, name, shopLabel) => {
    await addDoc(collection(db, "syncQueue"), {
      employeeId,
      name,
      shop: shopLabel ?? SHOP_LABEL_FOR_SYNC[branchKey] ?? SHOP_LABEL_FOR_SYNC.main,
      action: "UPSERT_USER",
      status: "pending",
      createdAt: serverTimestamp(),
    });
  };

  const openAdd = useCallback(() => {
    setEditingId(null);
    setDetailDoc(null);
    setForm({
      EmployeeName: "",
      employeeId: "",
      employeeCode: "",
      phone: "",
      CompanyId: "1",
      Gender: "male",
      Status: "Working",
      EmployementType: "Permanent",
      department: "",
      monthlySalary: "",
      expectedStartTime: "09:30",
      lunchMinutes: "60",
      DOJ: null,
      portalPin: "",
    });
    setModalOpen(true);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      const target = e.target;
      const tag = String(target?.tagName || "").toLowerCase();
      const isTypingContext =
        target?.isContentEditable || tag === "input" || tag === "textarea" || tag === "select";
      if (!isTypingContext && e.key === "/") {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (!isTypingContext && canWrite && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        openAdd();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canWrite, openAdd]);

  const openEdit = (docSnap) => {
    setDetailDoc(null);
    const data = docSnap.data() || {};
    const monthly =
      data.monthlySalary ?? data.MonthlySalary ?? data.salary ?? data.Salary;
    setEditingId(docSnap.id);
    setForm({
      EmployeeName: String(data.EmployeeName ?? ""),
      employeeId: String(data.employeeId ?? ""),
      employeeCode: String(data.employeeCode ?? data.EmployeeCode ?? ""),
      phone: String(data.phone ?? ""),
      CompanyId: String(data.CompanyId ?? "1"),
      Gender: String(data.Gender ?? "male"),
      Status: String(data.Status ?? "Working"),
      EmployementType: String(data.EmployementType ?? data.EmploymentType ?? "Permanent"),
      department: String(data.department ?? data.Department ?? "").trim(),
      monthlySalary: monthly == null ? "" : String(monthly),
      expectedStartTime: String(data.expectedStartTime ?? "09:30").trim() || "09:30",
      lunchMinutes: String(data.lunchMinutes ?? 60),
      DOJ: toDateFromDynamic(data.DOJ),
      portalPin: String(data.portalPin ?? data.employeePortalPin ?? ""),
    });
    setModalOpen(true);
  };

  const saveEmployee = async () => {
    const name = form.EmployeeName.trim();
    const empIdTrim = form.employeeId.trim();
    const phone = form.phone.trim();
    if (!name || !empIdTrim || !phone) {
      toast.error("Please fill required fields (Name, Employee ID, Phone)");
      return;
    }
    let monthlySalary = null;
    const sal = form.monthlySalary.trim();
    if (sal) {
      const n = parseFloat(sal.replace(/,/g, ""));
      if (Number.isNaN(n)) {
        toast.error("Invalid salary value");
        return;
      }
      monthlySalary = n;
    }

    const data = {
      EmployeeName: name,
      employeeId: empIdTrim,
      employeeCode: String(form.employeeCode ?? "").trim(),
      user_id: empIdTrim,
      phone,
      Status: form.Status,
      Gender: form.Gender,
      CompanyId: form.CompanyId.trim(),
      EmployementType: form.EmployementType,
    };

    const dept = String(form.department || "").trim();
    data.department = dept;
    data.Department = dept;

    if (form.DOJ) {
      data.DOJ = Timestamp.fromDate(form.DOJ);
    } else if (!editingId) {
      data.DOJ = serverTimestamp();
    }

    if (monthlySalary != null) {
      data.monthlySalary = monthlySalary;
    }

    const exp = form.expectedStartTime.trim();
    data.expectedStartTime = exp || "09:30";
    data.lunchMinutes = parseInt(form.lunchMinutes, 10) || 60;

    const portalPin = String(form.portalPin ?? "").trim();
    if (portalPin) {
      data.portalPin = portalPin;
      data.employeePortalPin = portalPin;
    } else if (editingId) {
      data.portalPin = "";
      data.employeePortalPin = "";
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, empCollectionName, editingId), data);
        toast.success("Updated");
      } else {
        await addDoc(collection(db, empCollectionName), data);
        toast.success("Added");
      }
      await enqueueDeviceSync(empIdTrim, name, SHOP_LABEL_FOR_SYNC[branchKey]);
      setModalOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(`Save failed: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async (id) => {
    if (!canWrite || !empCollectionName) return;
    try {
      await deleteDoc(doc(db, empCollectionName, id));
      toast.success("Deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(String(e?.message || e));
    }
  };

  const syncOne = async (data) => {
    const empId = String(data.employeeId ?? "").trim();
    const name = String(data.EmployeeName ?? data.name ?? "-").trim();
    if (!empId) {
      toast.error("No employeeId");
      return;
    }
    try {
      await enqueueDeviceSync(empId, name, SHOP_LABEL_FOR_SYNC[branchKey]);
      toast.success("Sync queued");
    } catch (e) {
      toast.error(String(e?.message || e));
    }
  };

  const callPhone = (phone) => {
    const p = String(phone || "").replace(/\s/g, "");
    if (!p) {
      toast.error("No phone");
      return;
    }
    window.location.href = `tel:${p}`;
  };

  const empTypeOptions = useMemo(() => {
    const t = form.EmployementType;
    if (EMP_TYPES_BASE.includes(t)) return EMP_TYPES_BASE;
    return [t, ...EMP_TYPES_BASE];
  }, [form.EmployementType]);

  const departmentSelectOptions = useMemo(() => {
    const d = String(form.department || "").trim();
    if (d && !DEPARTMENT_BASE.includes(d)) return [d, ...DEPARTMENT_BASE];
    return DEPARTMENT_BASE;
  }, [form.department]);

  const hasActiveViewFilters = useMemo(
    () =>
      search.trim() ||
      statusFilter !== "All" ||
      departmentFilter !== "All" ||
      sortKey !== "name" ||
      sortDir !== "asc",
    [search, statusFilter, departmentFilter, sortKey, sortDir]
  );

  const exportCsv = useCallback(() => {
    const branchLabel = attendanceBranch?.label || branchKey;
    const rows = sortedFiltered.map((d) => {
      const data = d.data() || {};
      const doj = toDateFromDynamic(data.DOJ);
      return {
        name: data.EmployeeName ?? "",
        employeeId: data.employeeId ?? "",
        employeeCode: data.employeeCode ?? data.EmployeeCode ?? "",
        phone: data.phone ?? "",
        department: data.department ?? data.Department ?? "",
        status: data.Status ?? "",
        employmentType: data.EmployementType ?? data.EmploymentType ?? "",
        doj: doj ? formatDoj(doj) : "",
        monthlySalary: data.monthlySalary ?? data.MonthlySalary ?? data.salary ?? "",
        expectedStartTime: data.expectedStartTime ?? "",
        lunchMinutes: data.lunchMinutes ?? "",
        branch: branchLabel,
      };
    });
    downloadCsv(
      `employees-${branchLabel.replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "name", header: "Name" },
        { key: "employeeId", header: "Employee ID" },
        { key: "employeeCode", header: "Employee code" },
        { key: "phone", header: "Phone" },
        { key: "department", header: "Department" },
        { key: "status", header: "Status" },
        { key: "employmentType", header: "Employment type" },
        { key: "doj", header: "DOJ" },
        { key: "monthlySalary", header: "Monthly salary" },
        { key: "expectedStartTime", header: "Expected start" },
        { key: "lunchMinutes", header: "Lunch minutes" },
        { key: "branch", header: "Branch" },
      ],
      rows
    );
    toast.success(`Exported ${rows.length} rows`);
  }, [sortedFiltered, attendanceBranch?.label, branchKey]);

  const payrollShopQuery = encodeURIComponent(attendanceBranch?.label || "Main Shop");

  const inputClass =
    "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-500/25";
  const avatarClass =
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-violet-800 text-[10px] font-bold text-white ring-1 ring-white";

  return (
    <div className="min-h-screen bg-slate-100/90 text-slate-900 antialiased [font-feature-settings:'tnum'_on]">
      <Toaster position="top-right" toastOptions={{ className: "text-sm font-medium" }} />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto w-full max-w-[1600px] px-2 pb-20 pt-2 sm:px-3 lg:px-4">

        {!canWrite && (
          <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden />
            <div>
              <p>
                <span className="font-semibold">View only.</span> Payroll permission required to edit.
              </p>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
          <header className="bg-gradient-to-br from-indigo-700 via-indigo-700 to-violet-900 px-3 py-2.5 text-white sm:px-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="shrink-0 rounded-lg bg-white/15 p-1.5">
                    <Users className="h-4 w-4 text-indigo-100" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-base font-bold tracking-tight">Employees</h1>
                    <p className="text-[11px] text-indigo-100/90">
                      <span className="font-semibold text-white">{attendanceBranch?.label}</span>
                      <span className="mx-1 opacity-50">·</span>
                      <span className="tabular-nums">{stats.total}</span> on roster
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
                  {visibleBranchTabs.length > 1 ? (
                    <div
                      className="inline-flex flex-wrap gap-0.5 rounded-lg bg-white/10 p-0.5"
                      role="tablist"
                      aria-label="Branch"
                    >
                      {visibleBranchTabs.map((b) => (
                        <button
                          key={b.key}
                          type="button"
                          role="tab"
                          aria-selected={branchKey === b.key}
                          onClick={() => setBranchKey(b.key)}
                          className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition ${
                            branchKey === b.key
                              ? "bg-white text-indigo-900 shadow-sm"
                              : "text-indigo-100 hover:bg-white/10"
                          }`}
                        >
                          {b.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {canWrite ? (
                    <button
                      type="button"
                      onClick={openAdd}
                      className="inline-flex items-center justify-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-semibold text-indigo-900 shadow-sm hover:bg-indigo-50"
                    >
                      <UserPlus className="h-3.5 w-3.5" aria-hidden />
                      Add
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                <Link
                  to={`/payroll?shop=${payrollShopQuery}`}
                  className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold hover:bg-white/20"
                >
                  <CalendarRange className="h-3 w-3 opacity-90" aria-hidden />
                  Attendance
                </Link>
                <Link
                  to="/payroll/reports"
                  className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold hover:bg-white/20"
                >
                  <BarChart3 className="h-3 w-3 opacity-90" aria-hidden />
                  Reports
                </Link>
                <Link
                  to="/employee-portal"
                  className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold hover:bg-white/20"
                >
                  <ExternalLink className="h-3 w-3 opacity-80" aria-hidden />
                  Portal
                </Link>
              </div>
            </div>
          </header>

          <div className="border-b border-slate-200 bg-slate-50/80 px-3 py-2 sm:px-4">
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              <div className="flex flex-col rounded-lg border border-slate-200 bg-white px-2 py-1.5 shadow-sm">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">Roster</span>
                  <Users className="h-3 w-3 text-slate-400" aria-hidden />
                </div>
                <div className="text-lg font-bold tabular-nums text-slate-900">{stats.total}</div>
                <p className="text-[9px] leading-snug text-slate-500">
                  {sortedFiltered.length === docs.length
                    ? "Full directory"
                    : `${sortedFiltered.length} of ${docs.length} match`}
                </p>
              </div>
              {[
                {
                  label: "Working",
                  value: stats.working,
                  filter: "Working",
                  Icon: UserCheck,
                  active: "border-indigo-300/80 bg-indigo-50/60 ring-1 ring-indigo-200/70",
                  iconClass: "text-emerald-600",
                },
                {
                  label: "On leave",
                  value: stats.onLeave,
                  filter: "On Leave",
                  Icon: Calendar,
                  active: "border-amber-300/80 bg-amber-50/40 ring-1 ring-amber-200/60",
                  iconClass: "text-amber-700",
                },
                {
                  label: "Left",
                  value: stats.left,
                  filter: "Left",
                  Icon: UserX,
                  active: "border-rose-300/80 bg-rose-50/40 ring-1 ring-rose-200/60",
                  iconClass: "text-rose-600",
                },
              ].map((s) => {
                const active = statusFilter === s.filter;
                return (
                  <button
                    key={s.filter}
                    type="button"
                    onClick={() => setStatusFilter(active ? "All" : s.filter)}
                    className={`flex flex-col rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-left shadow-sm transition hover:border-slate-300 ${
                      active ? s.active : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">{s.label}</span>
                      <s.Icon className={`h-3 w-3 ${s.iconClass}`} aria-hidden />
                    </div>
                    <div className="text-lg font-bold tabular-nums text-slate-900">{s.value}</div>
                    <p className="text-[9px] font-medium text-slate-400">{active ? "Clear" : "Filter"}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2 p-2 sm:p-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden />
                  <input
                    ref={searchInputRef}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, ID, code, phone, department…"
                    className={`${inputClass} w-full pl-8 pr-8`}
                  />
                  {search.trim() ? (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center">
                  <div className="flex items-center gap-1">
                    <ArrowUpDown className="hidden h-3.5 w-3.5 text-slate-400 sm:block" aria-hidden />
                    <select
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value)}
                      className={`${inputClass} min-w-[9rem] font-medium text-slate-800`}
                      aria-label="Sort by"
                    >
                      {SORT_KEYS.map((o) => (
                        <option key={o.value} value={o.value}>
                          Sort: {o.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                      title={sortDir === "asc" ? "Ascending" : "Descending"}
                    >
                      {sortDir === "asc" ? "A → Z" : "Z → A"}
                    </button>
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={`${inputClass} font-medium text-slate-800 sm:min-w-[148px]`}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        Status: {s}
                      </option>
                    ))}
                  </select>
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className={`${inputClass} font-medium text-slate-800 sm:min-w-[160px]`}
                  >
                    <option value="All">Department: All</option>
                    {departmentOptions.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
                    <button
                      type="button"
                      title="Table view"
                      onClick={() => setViewMode("table")}
                      className={`rounded p-1 ${viewMode === "table" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    >
                      <List className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      title="Card view"
                      onClick={() => setViewMode("cards")}
                      className={`rounded p-1 ${viewMode === "cards" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    >
                      <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={exportCsv}
                    disabled={!sortedFiltered.length}
                    className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("All");
                      setDepartmentFilter("All");
                      setSortKey("name");
                      setSortDir("asc");
                    }}
                    disabled={!hasActiveViewFilters}
                    className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Reset search, filters and sorting"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-500">
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="font-semibold uppercase tracking-wide text-slate-400">Collection</span>
                <code className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-[10px] text-slate-700">
                  {empCollectionName || "…"}
                </code>
                <span className="hidden text-slate-400 sm:inline">·</span>
                <span className="text-slate-500">
                  <kbd className="rounded border border-slate-200 bg-white px-1 font-mono text-[10px]">/</kbd> search
                  {canWrite ? (
                    <>
                      {" "}
                      · <kbd className="rounded border border-slate-200 bg-white px-1 font-mono text-[10px]">N</kbd> new
                    </>
                  ) : null}
                </span>
              </span>
              {!loading && docs.length > 0 ? (
                <span className="rounded-full bg-white px-2.5 py-1 font-semibold text-slate-600 ring-1 ring-slate-200">
                  <span className="tabular-nums text-slate-900">{sortedFiltered.length}</span> shown
                  {search !== deferredSearch ? " (filtering…)" : null}
                </span>
              ) : null}
            </div>

            {loading ? (
              <div className="space-y-2.5">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={`sk-${i}`}
                    className="flex animate-pulse items-center gap-4 rounded-2xl border border-zinc-100 bg-zinc-50/80 p-4"
                  >
                    <div className="h-11 w-11 shrink-0 rounded-full bg-zinc-200" />
                    <div className="min-w-0 flex-1 space-y-2.5">
                      <div className="h-4 max-w-xs rounded-lg bg-zinc-200" />
                      <div className="h-3 max-w-md rounded-lg bg-zinc-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : docs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-10 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-500 ring-1 ring-zinc-200/80">
                  <Users className="h-8 w-8" aria-hidden />
                </div>
                <p className="mt-5 text-lg font-semibold tracking-tight text-zinc-900">No employees in this branch</p>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-zinc-500">
                  Add your first record or switch location using the branch chips in the header.
                </p>
                {canWrite && (
                  <button
                    type="button"
                    onClick={openAdd}
                    className="mt-8 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700"
                  >
                    <UserPlus className="h-4 w-4" aria-hidden />
                    Add employee
                  </button>
                )}
              </div>
            ) : sortedFiltered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-10 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-zinc-400 shadow-sm ring-1 ring-zinc-200/80">
                  <Search className="h-7 w-7" aria-hidden />
                </div>
                <p className="mt-4 text-lg font-semibold text-zinc-900">No matches</p>
                <p className="mt-2 text-sm text-zinc-500">Try clearing search or widening the status filter.</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("All");
                    setDepartmentFilter("All");
                  }}
                  className="mt-6 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  Clear filters
                </button>
              </div>
            ) : viewMode === "cards" ? (
              <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {sortedFiltered.map((d) => {
                  const data = d.data() || {};
                  const doj = toDateFromDynamic(data.DOJ);
                  const status = String(data.Status ?? "N/A");
                  const department = String(data.department ?? data.Department ?? "").trim();
                  const initial = employeeInitial(data.EmployeeName);
                  return (
                    <li
                      key={d.id}
                      className="flex cursor-pointer flex-col rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm transition hover:border-indigo-200"
                      onClick={() => setDetailDoc(d)}
                      onKeyDown={(e) => e.key === "Enter" && setDetailDoc(d)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`${avatarClass} h-9 w-9`}>{initial}</div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-zinc-900">{data.EmployeeName ?? "—"}</span>
                            <span
                              className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusBadgeClasses(status)}`}
                            >
                              {status}
                            </span>
                          </div>
                          <p className="mt-1 font-mono text-[11px] text-zinc-500">
                            ID {data.employeeId ?? "—"}
                            {(data.employeeCode ?? data.EmployeeCode) ? (
                              <span className="text-zinc-400"> · Code {data.employeeCode ?? data.EmployeeCode}</span>
                            ) : null}
                          </p>
                          {department ? (
                            <span className="mt-2 inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-900 ring-1 ring-indigo-200/70">
                              {department}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-zinc-600">
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3 text-zinc-400" aria-hidden />
                          {data.phone ?? "—"}
                        </span>
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <IndianRupee className="h-3 w-3 text-zinc-400" aria-hidden />
                          {formatSalary(data).replace("₹", "")}
                        </span>
                        {doj ? (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-zinc-400" aria-hidden />
                            {formatDoj(doj)}
                          </span>
                        ) : null}
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3 text-zinc-400" aria-hidden />
                          {data.expectedStartTime ?? "09:30"}
                        </span>
                      </div>
                      <div
                        className="mt-3 flex items-center justify-end gap-1 border-t border-zinc-100 pt-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canWrite ? (
                          <button
                            type="button"
                            onClick={() => openEdit(d)}
                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 shadow-sm hover:border-indigo-200 hover:bg-indigo-50/50"
                          >
                            Edit
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="max-h-[min(70vh,720px)] overflow-auto">
                  <table className="w-full min-w-[880px] border-collapse text-left text-xs">
                    <thead className="sticky top-0 z-10 bg-slate-800 text-[9px] font-semibold uppercase tracking-wide text-slate-100">
                      <tr>
                        <th className="px-2 py-1.5 pl-3">Employee</th>
                        <th className="px-2 py-1.5">ID</th>
                        <th className="px-2 py-1.5">Code</th>
                        <th className="px-2 py-1.5">Phone</th>
                        <th className="px-2 py-1.5">Dept</th>
                        <th className="px-2 py-1.5 text-right">Salary</th>
                        <th className="px-2 py-1.5">Start</th>
                        <th className="px-2 py-1.5">Status</th>
                        <th className="px-2 py-1.5">DOJ</th>
                        <th className="px-2 py-1.5 pr-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {sortedFiltered.map((d) => {
                        const data = d.data() || {};
                        const doj = toDateFromDynamic(data.DOJ);
                        const status = String(data.Status ?? "N/A");
                        const department = String(data.department ?? data.Department ?? "").trim();
                        const initial = employeeInitial(data.EmployeeName);
                        return (
                          <tr
                            key={d.id}
                            className="cursor-pointer transition-colors hover:bg-slate-50"
                            onClick={() => setDetailDoc(d)}
                          >
                              <td className="px-2 py-1.5 pl-3">
                                <div className="flex items-center gap-2">
                                  <div className={avatarClass}>{initial}</div>
                                  <div className="min-w-0">
                                    <div className="truncate font-medium text-slate-900">{data.EmployeeName ?? "—"}</div>
                                    {(data.EmployementType || data.EmploymentType) && (
                                      <div className="truncate text-[10px] text-slate-500">
                                        {String(data.EmployementType ?? data.EmploymentType ?? "")}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-1.5 font-mono text-[10px] text-slate-600">{data.employeeId ?? "—"}</td>
                              <td className="px-2 py-1.5 font-mono text-[10px] text-slate-600">
                                {data.employeeCode ?? data.EmployeeCode ?? "—"}
                              </td>
                              <td className="max-w-[7rem] truncate px-2 py-1.5 text-slate-700">{data.phone ?? "—"}</td>
                              <td className="max-w-[5rem] truncate px-2 py-1.5 text-slate-700">{department || "—"}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-slate-800">{formatSalary(data)}</td>
                              <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-slate-600">
                                {data.expectedStartTime ?? "09:30"}
                              </td>
                              <td className="px-2 py-1.5">
                                <span
                                  className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${statusBadgeClasses(status)}`}
                                >
                                  {status}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-slate-600">{doj ? formatDoj(doj) : "—"}</td>
                              <td className="px-2 py-1.5 pr-3 text-right" onClick={(e) => e.stopPropagation()}>
                                <div className="inline-flex items-center justify-end gap-0.5">
                                  {canWrite && (
                                    <button
                                      type="button"
                                      onClick={() => openEdit(d)}
                                      className="rounded border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-800 hover:border-indigo-200 hover:bg-indigo-50/50"
                                    >
                                      Edit
                                    </button>
                                  )}
                                  <div className="relative" data-employee-action-menu>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenMenuId((id) => (id === d.id ? null : d.id));
                                      }}
                                      className="rounded p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                                      aria-expanded={openMenuId === d.id}
                                      aria-haspopup="menu"
                                      aria-label="More actions"
                                    >
                                      <MoreVertical className="h-4 w-4" aria-hidden />
                                    </button>
                                    {openMenuId === d.id ? (
                                      <div
                                        className="absolute right-0 z-20 mt-1.5 w-52 overflow-hidden rounded-xl border border-zinc-200/90 bg-white py-1.5 text-xs shadow-2xl ring-1 ring-zinc-950/5"
                                        role="menu"
                                      >
                                        <button
                                          type="button"
                                          role="menuitem"
                                          className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left font-medium text-zinc-700 transition hover:bg-zinc-50"
                                          onClick={() => {
                                            setOpenMenuId(null);
                                            callPhone(data.phone);
                                          }}
                                        >
                                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600">
                                            <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                          </span>
                                          Call
                                        </button>
                                        <button
                                          type="button"
                                          role="menuitem"
                                          className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left font-medium text-zinc-700 transition hover:bg-zinc-50"
                                          onClick={() => {
                                            setOpenMenuId(null);
                                            syncOne(data);
                                          }}
                                        >
                                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600">
                                            <Smartphone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                          </span>
                                          Sync to device
                                        </button>
                                        {canWrite && (
                                          <>
                                            <div className="mx-2 my-1 h-px bg-zinc-100" />
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left font-medium text-rose-600 transition hover:bg-rose-50"
                                              onClick={() => {
                                                setOpenMenuId(null);
                                                setDeleteTarget({
                                                  id: d.id,
                                                  name: String(data.EmployeeName ?? data.employeeId ?? "this employee"),
                                                });
                                              }}
                                            >
                                              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
                                                <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                              </span>
                                              Delete
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
            )}
          </div>
        </div>
      </main>

      {canWrite ? (
        <button
          type="button"
          onClick={openAdd}
          className="fixed bottom-5 right-4 z-50 flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md ring-2 ring-white/90 transition hover:bg-indigo-700 lg:hidden"
          title="Add employee"
          aria-label="Add employee"
        >
          <UserPlus className="h-5 w-5" strokeWidth={2} aria-hidden />
        </button>
      ) : null}

      {modalOpen && (
        <div
          className="fixed inset-0 z-[1300] flex items-end justify-center bg-zinc-950/60 p-3 backdrop-blur-sm sm:items-center"
          onClick={() => !saving && setModalOpen(false)}
          role="presentation"
        >
          <div
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-zinc-200/90 bg-white shadow-[0_32px_64px_-24px_rgba(24,24,27,0.35)] sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-modal-title"
            aria-describedby="employee-modal-desc"
          >
            <div className="border-l-[3px] border-l-indigo-600">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-100 bg-white/95 px-5 py-4 backdrop-blur-md sm:px-7 sm:py-5">
              <div className="flex min-w-0 items-start gap-3">
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-inner ${
                    editingId ? "bg-violet-100 text-violet-700 ring-1 ring-violet-200/80" : "bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200/80"
                  }`}
                >
                  {editingId ? <Pencil className="h-5 w-5" aria-hidden /> : <UserPlus className="h-5 w-5" aria-hidden />}
                </div>
                <div>
                  <p
                    id="employee-modal-desc"
                    className="text-[10px] font-bold uppercase tracking-widest text-zinc-400"
                  >
                    {editingId ? "Edit" : "Create"}
                  </p>
                  <h2 id="employee-modal-title" className="mt-1 text-xl font-bold tracking-tight text-zinc-900 sm:text-2xl">
                    {editingId ? "Employee details" : "New employee"}
                  </h2>
                  <p className="mt-1 text-xs font-medium text-zinc-500">{attendanceBranch?.label}</p>
                </div>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => setModalOpen(false)}
                className="rounded-xl border border-zinc-200 bg-zinc-50 p-2 text-zinc-600 transition hover:bg-zinc-100 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 px-5 py-6 text-sm sm:px-7">
              <section className="rounded-2xl border border-zinc-100 bg-zinc-50/50 p-4 ring-1 ring-zinc-950/[0.02] sm:p-5">
                <h3 className="mb-4 border-b border-zinc-200/80 pb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                  Identity and contact
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="text-xs font-semibold text-zinc-700">Name *</span>
                    <input
                      className={`${inputClass} mt-1 w-full`}
                      value={form.EmployeeName}
                      onChange={(e) => setForm((f) => ({ ...f, EmployeeName: e.target.value }))}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Employee ID *</span>
                    <input
                      className={`${inputClass} mt-1 w-full font-mono text-sm`}
                      value={form.employeeId}
                      onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Employee code</span>
                    <input
                      className={`${inputClass} mt-1 w-full font-mono text-sm`}
                      value={form.employeeCode}
                      onChange={(e) => setForm((f) => ({ ...f, employeeCode: e.target.value }))}
                      placeholder="Punching machine / device code"
                    />
                    <span className="mt-0.5 block text-[10px] text-zinc-500">
                      Saved as <code className="rounded bg-zinc-100 px-0.5">employeeCode</code> on the employee document.
                    </span>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Phone *</span>
                    <input
                      type="tel"
                      className={`${inputClass} mt-1 w-full`}
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Company ID</span>
                    <input
                      className={`${inputClass} mt-1 w-full`}
                      value={form.CompanyId}
                      onChange={(e) => setForm((f) => ({ ...f, CompanyId: e.target.value }))}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Monthly salary (optional)</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${inputClass} mt-1 w-full`}
                      value={form.monthlySalary}
                      onChange={(e) => setForm((f) => ({ ...f, monthlySalary: e.target.value }))}
                    />
                  </label>
                </div>
              </section>

              <section className="rounded-2xl border border-zinc-100 bg-zinc-50/50 p-4 ring-1 ring-zinc-950/[0.02] sm:p-5">
                <h3 className="mb-4 border-b border-zinc-200/80 pb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                  Employment
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Gender</span>
                    <select
                      className={`${inputClass} mt-1 w-full`}
                      value={form.Gender}
                      onChange={(e) => setForm((f) => ({ ...f, Gender: e.target.value }))}
                    >
                      {GENDER_OPTIONS.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Status</span>
                    <select
                      className={`${inputClass} mt-1 w-full`}
                      value={form.Status}
                      onChange={(e) => setForm((f) => ({ ...f, Status: e.target.value }))}
                    >
                      {["Working", "On Leave", "Left"].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-xs font-semibold text-zinc-700">Employment type</span>
                    <select
                      className={`${inputClass} mt-1 w-full`}
                      value={form.EmployementType}
                      onChange={(e) => setForm((f) => ({ ...f, EmployementType: e.target.value }))}
                    >
                      {empTypeOptions.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-xs font-semibold text-zinc-700">Department (brand / counter)</span>
                    <select
                      className={`${inputClass} mt-1 w-full`}
                      value={form.department}
                      onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                    >
                      <option value="">Not set</option>
                      {departmentSelectOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <span className="mt-0.5 block text-[10px] text-zinc-500">
                      Saved as <code className="rounded bg-zinc-100 px-0.5">department</code> on the employee document.
                    </span>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-xs font-semibold text-zinc-700">Date of joining</span>
                    <input
                      type="date"
                      className={`${inputClass} mt-1 w-full`}
                      value={
                        form.DOJ
                          ? `${form.DOJ.getFullYear()}-${String(form.DOJ.getMonth() + 1).padStart(2, "0")}-${String(form.DOJ.getDate()).padStart(2, "0")}`
                          : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) {
                          setForm((f) => ({ ...f, DOJ: null }));
                          return;
                        }
                        const [y, m, d] = v.split("-").map(Number);
                        setForm((f) => ({ ...f, DOJ: new Date(y, m - 1, d) }));
                      }}
                    />
                  </label>
                </div>
              </section>

              <section className="rounded-2xl border border-zinc-100 bg-zinc-50/50 p-4 ring-1 ring-zinc-950/[0.02] sm:p-5">
                <h3 className="mb-4 border-b border-zinc-200/80 pb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                  Attendance rules
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Expected start (HH:mm)</span>
                    <input
                      className={`${inputClass} mt-1 w-full`}
                      placeholder="09:30"
                      value={form.expectedStartTime}
                      onChange={(e) => setForm((f) => ({ ...f, expectedStartTime: e.target.value }))}
                    />
                    <span className="mt-0.5 block text-[10px] text-zinc-500">Used for late report.</span>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-zinc-700">Lunch duration (minutes)</span>
                    <input
                      type="number"
                      className={`${inputClass} mt-1 w-full`}
                      value={form.lunchMinutes}
                      onChange={(e) => setForm((f) => ({ ...f, lunchMinutes: e.target.value }))}
                    />
                  </label>
                </div>
              </section>

              <section className="rounded-xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/80 via-white to-slate-50/30 p-4 ring-1 ring-indigo-100/80 sm:p-5">
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-indigo-900/90">Employee portal</h3>
                <p className="mb-4 text-xs leading-relaxed text-zinc-600">
                  Staff sign in at{" "}
                  <Link to="/employee-portal" className="font-semibold text-indigo-700 underline-offset-2 hover:underline">
                    /employee-portal
                  </Link>{" "}
                  with name or ID plus PIN.
                </p>
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-700">Portal PIN</span>
                  <input
                    type="password"
                    className={`${inputClass} mt-1 w-full border-indigo-200/80 bg-white`}
                    value={form.portalPin}
                    onChange={(e) => setForm((f) => ({ ...f, portalPin: e.target.value }))}
                    placeholder="Set PIN to enable — leave blank to disable"
                    autoComplete="new-password"
                  />
                  <span className="mt-0.5 block text-[10px] text-zinc-500">Saving empty clears PIN for this employee.</span>
                </label>
              </section>
            </div>

            <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-zinc-100 bg-white/95 px-5 py-4 backdrop-blur-md sm:flex-row sm:justify-end sm:px-7">
              <button
                type="button"
                disabled={saving}
                className="rounded-xl border border-zinc-200 bg-zinc-50 px-5 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-100 disabled:opacity-50"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
                onClick={saveEmployee}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                {saving ? "Saving…" : editingId ? "Save changes" : "Create employee"}
              </button>
            </div>
            <p className="pb-4 text-center text-[10px] font-medium uppercase tracking-wider text-zinc-400">Esc to close</p>
            </div>
          </div>
        </div>
      )}

      {detailDoc ? (
        <div
          className="fixed inset-0 z-[1320] flex justify-end bg-slate-900/40 backdrop-blur-sm"
          role="presentation"
          onClick={() => setDetailDoc(null)}
        >
          <aside
            className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="employee-detail-title"
          >
            {(() => {
              const data = detailDoc.data() || {};
              const doj = toDateFromDynamic(data.DOJ);
              const department = String(data.department ?? data.Department ?? "").trim();
              const status = String(data.Status ?? "N/A");
              return (
                <>
                  <div className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-800 text-lg font-bold text-white shadow-md">
                        {employeeInitial(data.EmployeeName)}
                      </div>
                      <div className="min-w-0">
                        <h2 id="employee-detail-title" className="truncate text-lg font-bold text-zinc-900">
                          {data.EmployeeName ?? "—"}
                        </h2>
                        <p className="font-mono text-xs text-zinc-500">
                          {data.employeeId ?? "—"}
                          {(data.employeeCode ?? data.EmployeeCode) ? (
                            <span className="text-zinc-400"> · {data.employeeCode ?? data.EmployeeCode}</span>
                          ) : null}
                        </p>
                        <span
                          className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusBadgeClasses(status)}`}
                        >
                          {status}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDetailDoc(null)}
                      className="rounded-xl border border-zinc-200 p-2 text-zinc-600 hover:bg-zinc-50"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
                    <dl className="grid grid-cols-2 gap-3">
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Employee code</dt>
                        <dd className="mt-0.5 font-mono font-medium text-zinc-900">
                          {data.employeeCode ?? data.EmployeeCode ?? "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Phone</dt>
                        <dd className="mt-0.5 font-medium text-zinc-900">{data.phone ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Department</dt>
                        <dd className="mt-0.5 font-medium text-zinc-900">{department || "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Salary</dt>
                        <dd className="mt-0.5 font-medium tabular-nums text-zinc-900">{formatSalary(data)}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">DOJ</dt>
                        <dd className="mt-0.5 font-medium text-zinc-900">{doj ? formatDoj(doj) : "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Start time</dt>
                        <dd className="mt-0.5 font-medium text-zinc-900">{data.expectedStartTime ?? "09:30"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Lunch (min)</dt>
                        <dd className="mt-0.5 font-medium text-zinc-900">{data.lunchMinutes ?? 60}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Employment</dt>
                        <dd className="mt-0.5 font-medium text-zinc-900">
                          {String(data.EmployementType ?? data.EmploymentType ?? "—")}
                          {data.Gender ? ` · ${data.Gender}` : ""}
                        </dd>
                      </div>
                    </dl>
                    <div className="rounded-xl border border-cyan-100 bg-cyan-50/50 p-3 text-xs text-zinc-600">
                      Portal PIN:{" "}
                      <span className="font-semibold text-zinc-900">
                        {data.portalPin || data.employeePortalPin ? "Set" : "Not set"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 border-t border-zinc-100 px-5 py-4">
                    <button
                      type="button"
                      onClick={() => callPhone(data.phone)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
                    >
                      <Phone className="h-4 w-4" aria-hidden />
                      Call
                    </button>
                    <button
                      type="button"
                      onClick={() => syncOne(data)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
                    >
                      <Smartphone className="h-4 w-4" aria-hidden />
                      Sync device
                    </button>
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => openEdit(detailDoc)}
                        className="w-full rounded-xl bg-zinc-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-zinc-800"
                      >
                        Edit employee
                      </button>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </aside>
        </div>
      ) : null}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-[1350] flex items-center justify-center bg-zinc-950/65 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-3xl border border-zinc-200/90 bg-white shadow-[0_28px_64px_-28px_rgba(24,24,27,0.35)] ring-1 ring-zinc-950/[0.03]"
            role="alertdialog"
            aria-labelledby="del-title"
            aria-describedby="del-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 bg-gradient-to-r from-rose-500 via-rose-400 to-amber-400" aria-hidden />
            <div className="p-6 sm:p-7">
              <div className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-700 ring-1 ring-rose-200/80">
                  <AlertTriangle className="h-6 w-6" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h2 id="del-title" className="text-lg font-bold tracking-tight text-zinc-900">
                    Delete employee?
                  </h2>
                  <p id="del-desc" className="mt-2 text-sm leading-relaxed text-zinc-600">
                    This removes <span className="font-semibold text-zinc-900">{deleteTarget.name}</span> from{" "}
                    <span className="font-mono text-xs text-zinc-500">{empCollectionName}</span>. Device sync is not reversed
                    automatically.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="rounded-xl border border-zinc-200 bg-zinc-50 px-5 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-100"
                  onClick={() => setDeleteTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-rose-500"
                  onClick={() => runDelete(deleteTarget.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
